import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { Loader2, Sparkle, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setAuthFromIntake } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/invite/${token}`);
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post(`/invite/${token}/claim`, {
        password,
        phone: phone || undefined,
      });
      const userResp = await http.get("/auth/me").catch(() => null);
      setAuthFromIntake(userResp?.data || data.user, data.access_token);
      if (data.phone_changed) {
        toast.success("Profile claimed! Phone updated for review.");
      } else {
        toast.success("Welcome to your client portal");
      }
      navigate("/dashboard");
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#141C16] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link to="/"><Brand variant="monogram" className="h-28 w-28 mx-auto" /></Link>
        </div>

        {loading && (
          <div className="frame-card p-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#D2A853]" /></div>
        )}

        {!loading && error && !info && (
          <div className="frame-card p-8 text-center" data-testid="invite-error">
            <span className="overline text-[#CF6679]">Invite Unavailable</span>
            <p className="mt-4 text-sm text-[#A3A8A4]">{error}</p>
            <Link to="/login" className="overline text-[#D2A853] inline-flex items-center gap-2 mt-6 hover:text-[#E2BC6A]" data-testid="invite-go-login">
              <ArrowLeft className="w-3 h-3" /> Back to Login
            </Link>
          </div>
        )}

        {!loading && info && (
          <div className="frame-card p-8 lg:p-12" data-testid="invite-card">
            <div className="text-center mb-8">
              <Sparkle className="w-4 h-4 mx-auto text-[#D2A853] mb-3" strokeWidth={1.4} />
              <span className="overline text-[#D2A853]">Welcome</span>
              <h2 className="font-display text-3xl text-[#E8E9E3] mt-3">Claim Your Profile</h2>
              <p className="text-sm text-[#A3A8A4] mt-3">
                Brian set up your profile at PAID DEWZ. Confirm your details and set a password to unlock the portal.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-6">
              <div>
                <label className="overline text-[#A3A8A4] block mb-2">Name</label>
                <div className="border-b border-[#D2A853]/20 py-3 text-[#E8E9E3]">{info.name}</div>
              </div>
              <div>
                <label className="overline text-[#A3A8A4] block mb-2">Email</label>
                <div className="border-b border-[#D2A853]/20 py-3 text-[#E8E9E3]">{info.email}</div>
                <p className="text-[10px] text-[#5A665D] mt-2">By clicking this invite link you've verified this email.</p>
              </div>
              <div>
                <label className="overline text-[#A3A8A4] block mb-2">
                  Phone {info.phone_hint && <span className="text-[#5A665D]">(on file: {info.phone_hint})</span>}
                </label>
                <input
                  type="tel"
                  className="input-line"
                  placeholder={info.phone_hint || "+1 555 555 5555"}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="invite-phone"
                />
                <p className="text-[10px] text-[#5A665D] mt-2">Leave blank to keep the imported number, or update it.</p>
              </div>
              <div>
                <label className="overline text-[#A3A8A4] block mb-2">Set Password (8+ characters)</label>
                <input
                  type="password"
                  required
                  className="input-line"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="invite-password"
                />
              </div>
              {error && <div className="text-[#CF6679] text-sm" data-testid="invite-error">{error}</div>}
              <button
                type="submit"
                disabled={busy}
                className="btn-brass w-full flex items-center justify-center gap-2"
                data-testid="invite-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Claim Profile
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
